// src/libraries/ParametricDotsLibrary.js

const DEFAULT_LABELS = {
  3: ["UL", "UM", "UR", "LEM", "CENTER", "RM", "LL", "LM", "LR"],
  4: [
    "P1", "P2", "P3", "P4",
    "P5", "P6", "P7", "P8",
    "P9", "P10", "P11", "P12",
    "P13", "P14", "P15", "P16",
  ],
  5: [
    "P1", "P2", "P3", "P4", "P5",
    "P6", "P7", "P8", "P9", "P10",
    "P11", "P12", "P13", "P14", "P15",
    "P16", "P17", "P18", "P19", "P20",
    "P21", "P22", "P23", "P24", "P25",
  ],
};



function getLayoutEngine() {
  return window.layoutEngine;
}

function getProjectManager() {
  return window.ProjectManager;
}

function getRefBBox(refName) {
  const layoutEngine = getLayoutEngine();
  if (!layoutEngine || typeof layoutEngine.getBoundingBox !== "function") return null;
  return layoutEngine.getBoundingBox(refName);
}

function getParentCell(parentCellName) {
  const layoutEngine = getLayoutEngine();
  if (!layoutEngine) return null;

  const merged = layoutEngine.getMergedCell?.(parentCellName);
  if (merged) return merged;

  const created = layoutEngine.getCellByName?.(parentCellName);
  const uploaded = layoutEngine.getUploadedCell?.(parentCellName);

  if (created && uploaded) {
    const byId = new Map();

    for (const inst of uploaded.instances || []) {
      if (!inst?.id) continue;
      byId.set(inst.id, inst);
    }

    for (const inst of created.instances || []) {
      if (!inst?.id) continue;
      byId.set(inst.id, inst);
    }

    return {
      ...uploaded,
      ...created,
      name: created.name || uploaded.name || parentCellName,
      elements: [
        ...(uploaded.elements || []),
        ...(created.elements || []),
      ],
      instances: Array.from(byId.values()),
    };
  }

  return created || uploaded || null;
}

function getGridStep(size) {
  if (size <= 1) return 0;
  return 1 / (size - 1);
}

function makeDotId(target, label) {
  return `${target.name || target.refName || "target"}::${label}`;
}

function cloneTarget(target) {
  return {
    name: target.name,
    refName: target.refName,
    instanceId: target.instanceId,
    parentCellName: target.parentCellName,
    x: target.x,
    y: target.y,
    width: target.width,
    height: target.height,
  };
}


function moveLiveInstances(parentCellName, instanceIds = [], dx = 0, dy = 0) {
  const layoutEngine = getLayoutEngine();
  if (!layoutEngine) return;

  const ids = new Set(instanceIds);
  if (!ids.size) return;

  const createdCell = layoutEngine.getCellByName?.(parentCellName);
  const uploadedCell = layoutEngine.getUploadedCell?.(parentCellName);

  const createdById = new Map();
  for (const inst of (createdCell?.instances || [])) {
    if (!inst?.id) continue;
    createdById.set(inst.id, inst);
  }

  const uploadedById = new Map();
  for (const inst of (uploadedCell?.instances || [])) {
    if (!inst?.id) continue;
    uploadedById.set(inst.id, inst);
  }

  for (const id of ids) {
    const targetInst = createdById.get(id) || uploadedById.get(id);
    if (!targetInst) continue;

    targetInst.x = (targetInst.x || 0) + dx;
    targetInst.y = (targetInst.y || 0) + dy;
  }
}

function getInstanceCenter(parentCell, inst) {
  if (!parentCell || !inst) return { x: inst?.x || 0, y: inst?.y || 0 };

  const refBBox = getRefBBox(inst.ref);
  if (!refBBox) {
    return { x: inst.x || 0, y: inst.y || 0 };
  }

  return {
    x: (inst.x || 0) + (refBBox.x || 0) + (refBBox.width || 0) / 2,
    y: (inst.y || 0) + (refBBox.y || 0) + (refBBox.height || 0) / 2,
  };
}

function normalizeVector(dx, dy) {
  const len = Math.sqrt(dx * dx + dy * dy);

  if (!Number.isFinite(len) || len < 0.000001) {
    return { x: 1, y: 0 };
  }

  return {
    x: dx / len,
    y: dy / len,
  };
}

function getParamSignedDirection(value, fallback = 1) {
  const n = Number(value);
  if (n > 0) return 1;
  if (n < 0) return -1;
  return fallback >= 0 ? 1 : -1;
}

function normalizeConnectionSpacingValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

const ParametricDotsLibrary = {
  targets: [],
  pointerDots: [],
  selectedDotA: null,
  selectedDotB: null,
  gridSize: 3,

  connections: [],
  connectionCounter: 1,

  frozenRefNames: new Set(),

  setFrozenRefName(refName, frozen = true) {
    if (!refName) return;

    if (!(this.frozenRefNames instanceof Set)) {
      this.frozenRefNames = new Set(this.frozenRefNames || []);
    }

    if (frozen) {
      this.frozenRefNames.add(refName);
    } else {
      this.frozenRefNames.delete(refName);
    }

    window.dispatchEvent(
      new CustomEvent("parametric-freeze-changed", {
        detail: {
          refName,
          frozen,
          frozenRefNames: Array.from(this.frozenRefNames),
        },
      })
    );
  },

  isFrozenRefName(refName) {
    if (!refName) return false;

    if (!(this.frozenRefNames instanceof Set)) {
      this.frozenRefNames = new Set(this.frozenRefNames || []);
    }

    return this.frozenRefNames.has(refName);
  },

  setTargets(targets = [], gridSize = 3) {
    this.targets = Array.isArray(targets) ? targets.map(cloneTarget) : [];
    this.gridSize = Number(gridSize) || 3;
    this.pointerDots = [];
    this.selectedDotA = null;
    this.selectedDotB = null;
  },

  emitConnectionsChanged() {
  window.dispatchEvent(
    new CustomEvent("parametric-connections-changed", {
      detail: { connections: this.connections },
    })
  );
},

getConnections() {
  return Array.isArray(this.connections) ? this.connections : [];
},

makeConnectionName() {
  const name = `CONN_${this.connectionCounter}`;
  this.connectionCounter += 1;
  return name;
},

findConnectionByName(name) {
  return this.connections.find((c) => c.name === name) || null;
},



selectConnectionDots(engine, connectionName) {
  const conn = this.findConnectionByName(connectionName);
  if (!conn) return false;

  const dotA = (this.pointerDots || []).find(
    (dot) =>
      dot.parentCellName === conn.parentCellName &&
      dot.instanceId === conn.instanceAId &&
      dot.label === conn.dotALabel
  );

  const dotB = (this.pointerDots || []).find(
    (dot) =>
      dot.parentCellName === conn.parentCellName &&
      dot.instanceId === conn.instanceBId &&
      dot.label === conn.dotBLabel
  );

  if (!dotA || !dotB) return false;

  this.selectedDotA = dotA;
  this.selectedDotB = dotB;

  if (engine) {
    this.applyToEngine(engine);

    // ✅ Bring selected connection into view
    const pad = 80;
    const minX = Math.min(dotA.wx, dotB.wx) - pad;
    const maxX = Math.max(dotA.wx, dotB.wx) + pad;
    const minY = Math.min(dotA.wy, dotB.wy) - pad;
    const maxY = Math.max(dotA.wy, dotB.wy) + pad;

    const rect = engine.canvas.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;

    const boxW = Math.max(1, maxX - minX);
    const boxH = Math.max(1, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const nextScale = Math.max(
      engine.minScale || 0.01,
      Math.min(
        engine.maxScale || 500,
        Math.min(viewW / boxW, viewH / boxH) * 0.9
      )
    );

    engine.scale = nextScale;
    engine.offsetX = viewW / 2 - cx * engine.scale;
    engine.offsetY = viewH / 2 - cy * engine.scale;

    engine.draw?.();
  }

  return true;
},

clearSelectedConnectionDots(engine) {
  this.selectedDotA = null;
  this.selectedDotB = null;

  if (engine) {
    this.applyToEngine(engine);
    engine.draw?.();
  }
},


getConnectionDots(conn) {
  if (!conn) return { dotA: null, dotB: null };

  const dotA = this.findPointerDot(
    conn.parentCellName,
    conn.instanceAId,
    conn.dotALabel
  );

  const dotB = this.findPointerDot(
    conn.parentCellName,
    conn.instanceBId,
    conn.dotBLabel
  );

  return { dotA, dotB };
},


setConnectionSpacing(engine, connectionName, axis, nextSpacingValue, paramName = "") {
  const conn = this.findConnectionByName(connectionName);
  if (!conn) return false;

  const key = axis === "y" ? "spacingY" : "spacingX";
  const paramKey = axis === "y" ? "spacingYParam" : "spacingXParam";

  const nextSpacing = normalizeConnectionSpacingValue(nextSpacingValue);
  const currentSpacing = normalizeConnectionSpacingValue(conn[key]);
  const diff = nextSpacing - currentSpacing;

  conn[paramKey] = paramName || "";

  if (Math.abs(diff) < 0.000001) {
    conn[key] = nextSpacing;
    this.emitConnectionsChanged();
    engine?.draw?.();
    return true;
  }

  const parentCell = getParentCell(conn.parentCellName);
  if (!parentCell) return false;

  const instA = (parentCell.instances || []).find(
    (inst) => inst.id === conn.instanceAId
  );

  const instB = (parentCell.instances || []).find(
    (inst) => inst.id === conn.instanceBId
  );

  if (!instA || !instB) return false;

  this.refreshTargetsFromParent(conn.parentCellName);
  this.generateDots(engine, this.gridSize);

  const { dotA, dotB } = this.getConnectionDots(conn);

  if (!dotA || !dotB) {
    alert("⚠️ Connection dots not found.");
    return false;
  }

  const targetSpacingX =
    axis === "x"
      ? nextSpacing
      : normalizeConnectionSpacingValue(conn.spacingX);

  const targetSpacingY =
    axis === "y"
      ? nextSpacing
      : normalizeConnectionSpacingValue(conn.spacingY);

  const targetDx = (conn.spacingDirX || 1) * targetSpacingX;
  const targetDy = (conn.spacingDirY || 1) * targetSpacingY;

  const currentDx = dotB.wx - dotA.wx;
  const currentDy = dotB.wy - dotA.wy;

  const isAFrozen =
    this.isFrozenRefName(conn.refAName) ||
    this.isFrozenRefName(instA.ref);

  const isBFrozen =
    this.isFrozenRefName(conn.refBName) ||
    this.isFrozenRefName(instB.ref);

  if (isAFrozen && isBFrozen) {
    alert("⚠️ Dono connected PCell frozen hain. Spacing change karne ke liye ek side unfreeze karo.");
    return false;
  }

  let moveIds = [];
  let dx = 0;
  let dy = 0;

  if (isBFrozen) {
    // ✅ B frozen hai, A side move hogi
    moveIds = this.getConnectedInstanceIdsExcept(
      conn.parentCellName,
      conn.instanceAId,
      conn.instanceBId
    );

    dx = currentDx - targetDx;
    dy = currentDy - targetDy;
  } else {
    // ✅ Default: B side move hogi
    moveIds = this.getConnectedInstanceIdsExcept(
      conn.parentCellName,
      conn.instanceBId,
      conn.instanceAId
    );

    dx = targetDx - currentDx;
    dy = targetDy - currentDy;
  }

  if (!Array.isArray(moveIds) || !moveIds.length) {
    moveIds = isBFrozen ? [conn.instanceAId] : [conn.instanceBId];
  }

  const frozenMovingInst = (parentCell.instances || []).find(
    (inst) => moveIds.includes(inst.id) && this.isFrozenRefName(inst.ref)
  );

  if (frozenMovingInst) {
    alert(`⚠️ "${frozenMovingInst.ref}" frozen hai, isliye spacing apply nahi ho sakti.`);
    return false;
  }

  moveLiveInstances(conn.parentCellName, moveIds, dx, dy);

  conn[key] = nextSpacing;

  this.refreshTargetsFromParent(conn.parentCellName);
  this.generateDots(engine, this.gridSize);
  this.applyToEngine(engine);
  this.emitConnectionsChanged();

  engine?.draw?.();
  return true;
},


getConnectedInstanceIds(parentCellName, startInstanceId) {
  if (!parentCellName || !startInstanceId) return [];

  const visited = new Set([startInstanceId]);
  const queue = [startInstanceId];

  while (queue.length) {
    const currentId = queue.shift();

    for (const conn of this.connections || []) {
      if (conn.parentCellName !== parentCellName) continue;

      let nextId = null;

      if (conn.instanceAId === currentId) {
        nextId = conn.instanceBId;
      } else if (conn.instanceBId === currentId) {
        nextId = conn.instanceAId;
      }

      if (nextId && !visited.has(nextId)) {
        visited.add(nextId);
        queue.push(nextId);
      }
    }
  }

  return Array.from(visited);
},


getConnectedInstanceIdsExcept(parentCellName, startInstanceId, blockedInstanceId) {
  if (!parentCellName || !startInstanceId) return [];

  const visited = new Set([startInstanceId]);
  const queue = [startInstanceId];

  while (queue.length) {
    const currentId = queue.shift();

    for (const conn of this.connections || []) {
      if (conn.parentCellName !== parentCellName) continue;

      let nextId = null;

      if (conn.instanceAId === currentId) {
        nextId = conn.instanceBId;
      } else if (conn.instanceBId === currentId) {
        nextId = conn.instanceAId;
      }

      // ✅ changed/blocked instance ke through doosri side par mat jao
      if (!nextId || nextId === blockedInstanceId || visited.has(nextId)) continue;

      visited.add(nextId);
      queue.push(nextId);
    }
  }

  return Array.from(visited);
},

findPointerDot(parentCellName, instanceId, label) {
  return (this.pointerDots || []).find(
    (dot) =>
      dot.parentCellName === parentCellName &&
      dot.instanceId === instanceId &&
      dot.label === label
  ) || null;
},

maintainConnectionsForChangedInstance(engine, parentCellName, changedInstanceId) {
  if (!engine || !parentCellName || !changedInstanceId) return;

  const relatedConnections = (this.connections || []).filter(
    (conn) =>
      conn.parentCellName === parentCellName &&
      (conn.instanceAId === changedInstanceId || conn.instanceBId === changedInstanceId)
  );

  if (!relatedConnections.length) return;

  for (const conn of relatedConnections) {
    // fresh dots current geometry se banao
    this.refreshTargetsFromParent(parentCellName);
    this.generateDots(engine, this.gridSize);

    const changedIsA = conn.instanceAId === changedInstanceId;

    const fixedInstanceId = changedInstanceId;
    const fixedDotLabel = changedIsA ? conn.dotALabel : conn.dotBLabel;

    const moveRootId = changedIsA ? conn.instanceBId : conn.instanceAId;
    const movingDotLabel = changedIsA ? conn.dotBLabel : conn.dotALabel;

    const fixedDot = this.findPointerDot(parentCellName, fixedInstanceId, fixedDotLabel);
    const movingDot = this.findPointerDot(parentCellName, moveRootId, movingDotLabel);

    if (!fixedDot || !movingDot) continue;

    // ✅ moving side ko itna move karo ki uski connected dot changed pcell ki dot par aa jaye
    const dx = fixedDot.wx - movingDot.wx;
    const dy = fixedDot.wy - movingDot.wy;

    if (Math.abs(dx) < 0.000001 && Math.abs(dy) < 0.000001) continue;

    // ✅ Agar moving side bhi group hai to us poore side component ko move karo,
    // lekin changed middle instance ke through doosri side mat pakdo.
    const moveIds = this.getConnectedInstanceIdsExcept(
      parentCellName,
      moveRootId,
      changedInstanceId
    );

    moveLiveInstances(parentCellName, moveIds, dx, dy);
  }

  this.refreshTargetsFromParent(parentCellName);
  this.generateDots(engine, this.gridSize);
  this.applyToEngine(engine);
  this.emitConnectionsChanged();
},

removeConnectionsForInstanceIds(engine, parentCellName, instanceIds = []) {
  const ids = new Set(instanceIds);

  if (!ids.size) return;

  this.connections = (this.connections || []).filter((conn) => {
    if (conn.parentCellName !== parentCellName) return true;

    return !ids.has(conn.instanceAId) && !ids.has(conn.instanceBId);
  });

  this.refreshTargetsFromParent(parentCellName);

  if (engine) {
    this.generateDots(engine, this.gridSize);
    this.selectedDotA = null;
    this.selectedDotB = null;
    this.applyToEngine(engine);
  }

  this.emitConnectionsChanged();
},

  clear() {
    this.targets = [];
    this.pointerDots = [];
    this.selectedDotA = null;
    this.selectedDotB = null;
    this.gridSize = 3;
  },

  hasTargets() {
    return Array.isArray(this.targets) && this.targets.length > 0;
  },

  getParentCellName() {
    return this.targets.find(t => t?.parentCellName)?.parentCellName || null;
  },

  rebuildTargetsFromSelection(gridSize = 3) {
    const ProjectManager = getProjectManager();
    const parentCellName = ProjectManager?.getSelectedCell?.();
    if (!parentCellName) return [];

    const parentCell = getParentCell(parentCellName);
    if (!parentCell) return [];

    const selectedPatterns =
      window.usePlacementStore?.getState?.().selectedPatterns || [];

    const nextTargets = [];

    for (const refName of selectedPatterns) {
      const refBBox = getRefBBox(refName);
      if (!refBBox) continue;

      const insts = (parentCell.instances || []).filter(inst => inst.ref === refName);

      for (const inst of insts) {
        if (!inst.id) inst.id = `inst_${Date.now()}_${Math.random()}`;

        nextTargets.push({
          name: `${refName}__${inst.id}`,
          refName,
          instanceId: inst.id,
          parentCellName,
          x: (inst.x || 0) + (refBBox.x || 0),
          y: (inst.y || 0) + (refBBox.y || 0),
          width: refBBox.width,
          height: refBBox.height,
        });
      }
    }

    this.setTargets(nextTargets, gridSize);
    return nextTargets;
  },


  

  refreshTargetsFromParent(parentCellName) {
    if (!this.hasTargets()) return this.targets;

    const resolvedParent = parentCellName || this.getParentCellName();
    if (!resolvedParent) return this.targets;

    const parentCell = getParentCell(resolvedParent);
    if (!parentCell) return this.targets;

    const nextTargets = [];

    for (const oldTarget of this.targets) {
      if (!oldTarget?.instanceId || !oldTarget?.refName) continue;

      const inst = (parentCell.instances || []).find(
        item => item.id === oldTarget.instanceId
      );
      if (!inst) continue;

      const refBBox = getRefBBox(oldTarget.refName);
      if (!refBBox) continue;

      nextTargets.push({
        ...cloneTarget(oldTarget),
        parentCellName: resolvedParent,
        x: (inst.x || 0) + (refBBox.x || 0),
        y: (inst.y || 0) + (refBBox.y || 0),
        width: refBBox.width,
        height: refBBox.height,
      });
    }

    this.targets = nextTargets;
    return nextTargets;
  },

  generateDots(engine, gridSize = this.gridSize) {
    if (!engine || !this.hasTargets()) {
      this.pointerDots = [];
      return [];
    }

    const size = Number(gridSize) || 3;
    const labels = DEFAULT_LABELS[size] || [];
    const step = getGridStep(size);
    const dots = [];

    for (const target of this.targets) {
      const width = target.width || 0;
      const height = target.height || 0;

      let labelIndex = 0;

      for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
          const px = col * step;
          const py = row * step;

          const wx = target.x + width * px;
          const wy = target.y + height * py;
          const sx = engine.offsetX + wx * engine.scale;
          const sy = engine.offsetY + wy * engine.scale;

          const label = labels[labelIndex] || `P${labelIndex + 1}`;
          labelIndex += 1;

          dots.push({
  id: makeDotId(target, label),

  // ✅ new + old dono compatibility
  label,
  pointer: label,

  targetName: target.name,
  refName: target.refName,
  pattern: target.refName || target.name,

  instanceId: target.instanceId,
  parentCellName: target.parentCellName,
  row,
  col,

  wx,
  wy,
  sx,
  sy,

  // ✅ old engine hit-test compatibility
  x: sx,
  y: sy,
});
        }
      }
    }

    this.pointerDots = dots;
    this.gridSize = size;
    return dots;
  },

  redraw(engine, opts = {}) {
    if (!engine) return [];

    const gridSize = opts.gridSize || this.gridSize || 3;
    const parentCellName = opts.parentCellName || this.getParentCellName();

    if (opts.refresh !== false) {
      this.refreshTargetsFromParent(parentCellName);
    }

    return this.generateDots(engine, gridSize);
  },

  syncToEngine(engine) {
    if (!engine) return;
    engine.pointerDots = this.pointerDots;
    engine.selectedDotA = this.selectedDotA;
    engine.selectedDotB = this.selectedDotB;
    engine.dotGridSize = this.gridSize;
  },

  syncFromEngine(engine) {
    if (!engine) return;
    this.pointerDots = Array.isArray(engine.pointerDots) ? engine.pointerDots : [];
    this.selectedDotA = engine.selectedDotA || null;
    this.selectedDotB = engine.selectedDotB || null;
    this.gridSize = engine.dotGridSize || this.gridSize || 3;
  },

  applyToEngine(engine) {
    if (!engine) return;
    engine.pointerDots = this.pointerDots;
    engine.selectedDotA = this.selectedDotA;
    engine.selectedDotB = this.selectedDotB;
    engine.dotGridSize = this.gridSize;
  },

 findDotAtScreen(engine, sx, sy) {
  if (!engine || !Array.isArray(this.pointerDots)) return null;

  const hitRadius = engine.dotHitRadiusPx || 12;
  const hitRadiusSq = hitRadius * hitRadius;

  let best = null;
  let bestD2 = Infinity;

  for (const dot of this.pointerDots) {
    // ✅ IMPORTANT:
    // sx/sy ko cache se mat lo, zoom/pan ke baad stale ho jata hai.
    // current engine offset + scale se live screen position nikalo.
    const liveSx =
      engine.offsetX + Number(dot.wx ?? 0) * Number(engine.scale || 1);

    const liveSy =
      engine.offsetY + Number(dot.wy ?? 0) * Number(engine.scale || 1);

    // ✅ cache bhi update kar do, taaki baaki old code compatible rahe
    dot.sx = liveSx;
    dot.sy = liveSy;
    dot.x = liveSx;
    dot.y = liveSy;

    const dx = sx - liveSx;
    const dy = sy - liveSy;
    const d2 = dx * dx + dy * dy;

    if (d2 <= hitRadiusSq && d2 < bestD2) {
      best = dot;
      bestD2 = d2;
    }
  }

  return best;
},

  isSameDot(a, b) {
    if (!a || !b) return false;
    return a.id === b.id;
  },

  handleDotClick(engine, e) {
    if (!engine || !e) return false;

    const sx = e.offsetX;
    const sy = e.offsetY;
    const clicked = this.findDotAtScreen(engine, sx, sy);
    if (!clicked) return false;

    if (!this.selectedDotA) {
      this.selectedDotA = clicked;
    } else if (this.isSameDot(this.selectedDotA, clicked)) {
      this.selectedDotA = null;
      this.selectedDotB = null;
    } else if (!this.selectedDotB) {
      this.selectedDotB = clicked;
    } else if (this.isSameDot(this.selectedDotB, clicked)) {
      this.selectedDotB = null;
    } else {
      this.selectedDotA = this.selectedDotB;
      this.selectedDotB = clicked;
    }

    this.applyToEngine(engine);
    engine.draw?.();
    return true;
  },

connectSelectedDots(engine) {
  if (!engine || !this.selectedDotA || !this.selectedDotB) {
    alert("⚠️ Please select 2 dots first.");
    return;
  }

  const dotA = this.selectedDotA;
  const dotB = this.selectedDotB;

  if (!dotA.parentCellName || dotA.parentCellName !== dotB.parentCellName) {
    alert("⚠️ Dots must be inside same parent cell.");
    return;
  }

  if (!dotA.instanceId || !dotB.instanceId) {
    alert("⚠️ Dot instance id missing.");
    return;
  }

  if (dotA.instanceId === dotB.instanceId) {
    alert("⚠️ Same instance ke dots connect nahi kar sakte.");
    return;
  }

  const parentCell = getParentCell(dotA.parentCellName);

  if (!parentCell) {
    alert("⚠️ Parent cell not found.");
    return;
  }

  const instA = (parentCell.instances || []).find(
    (inst) => inst.id === dotA.instanceId
  );

  const instB = (parentCell.instances || []).find(
    (inst) => inst.id === dotB.instanceId
  );

  if (!instA || !instB) {
    alert("⚠️ Selected dot instance not found.");
    return;
  }

  const beforeA = {
    x: instA.x || 0,
    y: instA.y || 0,
  };

  const beforeB = {
    x: instB.x || 0,
    y: instB.y || 0,
  };

  const isAFrozen =
    this.isFrozenRefName(dotA.refName) ||
    this.isFrozenRefName(instA.ref);

  const isBFrozen =
    this.isFrozenRefName(dotB.refName) ||
    this.isFrozenRefName(instB.ref);

  if (isAFrozen && isBFrozen) {
    alert("⚠️ Dono PCell frozen hain. Connect karne ke liye kam se kam ek ko unfreeze karo.");
    return;
  }

  let moveIds = [];
  let dx = 0;
  let dy = 0;

  if (isAFrozen) {
    // ✅ A frozen hai: A apni jagah rahega, B A ke paas aayega
    const groupBIds = this.getConnectedInstanceIds(
      dotB.parentCellName,
      dotB.instanceId
    );

    moveIds =
      Array.isArray(groupBIds) && groupBIds.length
        ? groupBIds
        : [dotB.instanceId];

    dx = dotA.wx - dotB.wx;
    dy = dotA.wy - dotB.wy;
  } else {
    // ✅ Default behavior:
    // first selected dot / A wali shape second selected / B ke paas jayegi
    const groupAIds = this.getConnectedInstanceIds(
      dotA.parentCellName,
      dotA.instanceId
    );

    moveIds =
      Array.isArray(groupAIds) && groupAIds.length
        ? groupAIds
        : [dotA.instanceId];

    dx = dotB.wx - dotA.wx;
    dy = dotB.wy - dotA.wy;
  }

  // ✅ Safety: moving group me frozen PCell nahi honi chahiye
  const frozenMovingInst = (parentCell.instances || []).find(
    (inst) => moveIds.includes(inst.id) && this.isFrozenRefName(inst.ref)
  );

  if (frozenMovingInst) {
    alert(`⚠️ "${frozenMovingInst.ref}" frozen hai, isliye ye connected group move nahi ho sakta.`);
    return;
  }

  moveLiveInstances(dotA.parentCellName, moveIds, dx, dy);

  const connection = {
    name: this.makeConnectionName(),
    parentCellName: dotA.parentCellName,
    spacingX: 0,
  spacingY: 0,
  spacingXParam: "",
  spacingYParam: "",
  spacingDirX: dotB.wx > dotA.wx ? 1 : dotB.wx < dotA.wx ? -1 : 1,
  spacingDirY: dotB.wy > dotA.wy ? 1 : dotB.wy < dotA.wy ? -1 : 1,
    refAName: dotA.refName,
    refBName: dotB.refName,

    instanceAId: dotA.instanceId,
    instanceBId: dotB.instanceId,

    dotAId: dotA.id,
    dotBId: dotB.id,
    dotALabel: dotA.label || dotA.pointer || "",
    dotBLabel: dotB.label || dotB.pointer || "",

    beforeA,
    beforeB,

    createdAt: Date.now(),
  };

  this.connections.push(connection);

  this.refreshTargetsFromParent(dotA.parentCellName);
  this.generateDots(engine, this.gridSize);

  this.selectedDotA = null;
  this.selectedDotB = null;

  this.applyToEngine(engine);
  this.emitConnectionsChanged();

  engine.draw?.();
},





disconnectByName(engine, connectionName) {
  const connection = this.findConnectionByName(connectionName);
  if (!connection) {
    alert("⚠️ Connection not found.");
    return;
  }

  

  this.connections = this.connections.filter((c) => c.name !== connectionName);

  this.refreshTargetsFromParent(connection.parentCellName);

  if (engine) {
    this.generateDots(engine, this.gridSize);
    this.selectedDotA = null;
    this.selectedDotB = null;
    this.applyToEngine(engine);
    engine.draw?.();
  }

  this.emitConnectionsChanged();
},

draw(engine, options = {}) {
  const selectedRefName = String(options.selectedRefName || "");
  const dotsToDraw = selectedRefName
    ? (this.pointerDots || []).filter((dot) => dot.refName === selectedRefName)
    : this.pointerDots;

  if (!engine || !engine.ctx || !Array.isArray(dotsToDraw) || !dotsToDraw.length) {
    return;
  }

  const ctx = engine.ctx;
  const t = Date.now() / 220;
  const pulse = 1 + 0.22 * Math.sin(t);

  ctx.save();
  ctx.translate(engine.offsetX, engine.offsetY);
  ctx.scale(engine.scale, engine.scale);

  // ✅ Draw dotted spacing lines only when spacing > 0
  for (const conn of this.connections || []) {
    if (
      selectedRefName &&
      (conn.refAName !== selectedRefName || conn.refBName !== selectedRefName)
    ) {
      continue;
    }

    const spacingX = Number(conn.spacingX) || 0;
const spacingY = Number(conn.spacingY) || 0;

if (spacingX <= 0 && spacingY <= 0) continue;

    const { dotA, dotB } = this.getConnectionDots(conn);
    if (!dotA || !dotB) continue;

    ctx.beginPath();
    ctx.moveTo(dotA.wx, dotA.wy);
    ctx.lineTo(dotB.wx, dotB.wy);

    ctx.strokeStyle = "#00e5ff";
    ctx.lineWidth = 1.8 / engine.scale;
    ctx.setLineDash([7 / engine.scale, 5 / engine.scale]);
    ctx.lineDashOffset = (-Date.now() / 80) / engine.scale;
    ctx.stroke();
    ctx.setLineDash([]);
  }


  // ✅ Strong highlighted connection line
  // if (this.selectedDotA && this.selectedDotB) {
  //   // outer glow line
  //   ctx.beginPath();
  //   ctx.moveTo(this.selectedDotA.wx, this.selectedDotA.wy);
  //   ctx.lineTo(this.selectedDotB.wx, this.selectedDotB.wy);
  //   ctx.strokeStyle = "rgba(0,229,255,0.22)";
  //   ctx.lineWidth = (10 * pulse) / engine.scale;
  //   ctx.stroke();

  //   // bright animated dashed line
  //   ctx.beginPath();
  //   ctx.moveTo(this.selectedDotA.wx, this.selectedDotA.wy);
  //   ctx.lineTo(this.selectedDotB.wx, this.selectedDotB.wy);
  //   ctx.strokeStyle = "#00e5ff";
  //   ctx.lineWidth = 2.2 / engine.scale;
  //   ctx.setLineDash([10 / engine.scale, 6 / engine.scale]);
  //   ctx.lineDashOffset = (-Date.now() / 60) / engine.scale;
  //   ctx.stroke();
  //   ctx.setLineDash([]);
  // }

  for (const d of dotsToDraw) {
    const isA = this.selectedDotA && this.selectedDotA.id === d.id;
    const isB = this.selectedDotB && this.selectedDotB.id === d.id;
    const isSelected = isA || isB;

    let fill = "#00e5ff";
    if (isA) fill = "#ff3b3b";
    else if (isB) fill = "#39ff14";

    // glow halo
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(d.wx, d.wy, (10 * pulse) / engine.scale, 0, Math.PI * 2);
      ctx.fillStyle = isA ? "rgba(255,59,59,0.22)" : "rgba(57,255,20,0.22)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(d.wx, d.wy, (6.5 * pulse) / engine.scale, 0, Math.PI * 2);
      ctx.fillStyle = isA ? "rgba(255,59,59,0.35)" : "rgba(57,255,20,0.35)";
      ctx.fill();
    }

    // main dot
    ctx.beginPath();
    ctx.arc(d.wx, d.wy, (isSelected ? 4.8 : 3) / engine.scale, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();

    // white ring for selected
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(d.wx, d.wy, (7.2 * pulse) / engine.scale, 0, Math.PI * 2);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.4 / engine.scale;
      ctx.stroke();
    }

    ctx.font = `${10 / engine.scale}px monospace`;
    ctx.fillStyle = isSelected ? "#ffffff" : "#00e5ff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    const text = d.label || d.pointer || "";
    ctx.fillText(text, d.wx + 4 / engine.scale, d.wy + 4 / engine.scale);
  }

  ctx.restore();
}
};

export default ParametricDotsLibrary;
